import { useSyncExternalStore } from 'react';
import type { IRetryPolicy } from '@microsoft/signalr';
import { featuresQuery } from '@/api/features';
import { fetchPriceSnapshot } from '@/api/prices';
import type { Features } from '@/api/schemas';
import { queryClient } from '@/app/queryClient';
import { apiUrl } from '@/config';
import type { Market } from '@/domain/side';
import { currentExchangeSource, type ExchangeId, type ExchangeSource } from '@/session/currentExchange';
import { getSession, subscribeSession } from '@/session/session';
import { priceStore, type PriceStore, type Tick } from '@/realtime/priceStore';
import { delayedAfterMs, isSnapshotStalled, retryDelayMs, startTimeoutMs } from '@/realtime/timing';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** The part of a SignalR HubConnection the feed uses, so tests can pass a fake. */
export interface PriceHubConnection {
  start(): Promise<void>;
  stop(): Promise<void>;
  on(methodName: string, handler: (...args: unknown[]) => void): void;
  onclose(callback: (error?: Error) => void): void;
  onreconnecting(callback: (error?: Error) => void): void;
  onreconnected(callback: (connectionId?: string) => void): void;
}

/** Builds one connection to `url`; SignalR's own reconnects follow `retryPolicy`. */
export type ConnectionFactory = (url: string, retryPolicy: IRetryPolicy) => PriceHubConnection | Promise<PriceHubConnection>;

/** One snapshot item: a price, and when the exchange produced it (Unix ms, its clock; absent on an older API). */
export type SnapshotItem = Tick & { eventTime?: number | undefined };

/** A market's prices on an exchange to seed the store with, or null when the API has no snapshot for it. */
export type SnapshotLoader = (market: Market, signal: AbortSignal, exchange?: ExchangeId) => Promise<readonly SnapshotItem[] | null>;

export type PriceFeedOptions = {
  createConnection?: ConnectionFactory;
  loadSnapshot?: SnapshotLoader;
  store?: PriceStore;
  /** Whose prices to show: the signed-in account's exchange (src/session/currentExchange.ts) unless a test passes its own. */
  exchange?: ExchangeSource;
};

/**
 * The hub for an exchange's prices. Binance's spot prices go to every client; asking with ?feeds=usdm adds its futures
 * (an older server ignores it), ?feeds=okx gives OKX's spot and swaps instead. No token is needed.
 */
export function priceHubUrlFor(exchange: ExchangeId): string {
  return `${apiUrl}/priceHub?feeds=${exchange === 'okx' ? 'okx' : 'usdm'}`;
}

export const priceHubUrl = priceHubUrlFor('binance');

/**
 * The hub events of each exchange, each with one argument: the symbols of that market whose price changed in the last
 * second. On OKX, "usdm" holds its USDT perpetual swaps: the screens call the futures market usdm on either exchange.
 */
const priceEvents: Record<ExchangeId, Record<Market, string>> = {
  binance: { spot: 'ReceivePrice', usdm: 'ReceiveFuturesPrice' },
  okx: { spot: 'ReceiveOkxPrice', usdm: 'ReceiveOkxSwapPrice' },
};

const markets: readonly Market[] = ['spot', 'usdm'];

// ---------------------------------------------------------------------------------------------------------------------
// Connection state, for the health pill

let state: ConnectionState = 'disconnected';
let connectedAt: number | null = null;
const stateListeners = new Set<() => void>();

function setState(next: ConnectionState, at: number | null = next === 'connected' ? Date.now() : null): void {
  if (next === state && at === connectedAt) return;

  state = next;
  connectedAt = at;
  for (const listener of [...stateListeners]) listener();
}

export function getConnectionState(): ConnectionState {
  return state;
}

/** When the connection was last (re)established, or null while it is not connected. */
export function getConnectedAt(): number | null {
  return connectedAt;
}

export function subscribeConnectionState(listener: () => void): () => void {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
}

export function useConnectionState(): ConnectionState {
  return useSyncExternalStore(subscribeConnectionState, getConnectionState, getConnectionState);
}

// ---------------------------------------------------------------------------------------------------------------------
// Defaults: the real SignalR client and the real snapshot

/** The real client, loaded on first connect so it stays out of the first download. WebSockets and JSON. */
export const createSignalRConnection: ConnectionFactory = async (url, retryPolicy) => {
  const { HubConnectionBuilder, HttpTransportType, JsonHubProtocol, LogLevel } = await import('@microsoft/signalr');

  return new HubConnectionBuilder()
    .withUrl(url, { transport: HttpTransportType.WebSockets, withCredentials: false })
    .withHubProtocol(new JsonHubProtocol())
    .withAutomaticReconnect(retryPolicy)
    .configureLogging(LogLevel.Warning)
    .build();
};

/**
 * What GET /Features says. When asking fails (a failure other than the older API's 404), the last good answer, or null
 * when there is none yet.
 */
async function knownFeatures(): Promise<Features | null> {
  try {
    // No retry here: on a failure the snapshot is tried anyway, without waiting.
    return await queryClient.fetchQuery({ ...featuresQuery, retry: false });
  } catch {
    return queryClient.getQueryData(featuresQuery.queryKey) ?? null;
  }
}

/**
 * A market's snapshot when the API has one (GET /Features says priceSnapshot for spot, futuresPriceFeed for usdm,
 * okxPrices for OKX's), otherwise null. When /Features cannot be asked, the snapshot is tried anyway: an API without it
 * answers 404 or 400, which the caller treats like no snapshot. So a passing /Features failure never costs the snapshot.
 */
export async function loadPriceSnapshot(market: Market, signal: AbortSignal, exchange: ExchangeId = 'binance'): Promise<readonly SnapshotItem[] | null> {
  const features = await knownFeatures();
  if (signal.aborted) return null;

  if (exchange === 'okx') {
    if (features && !features.okxPrices) return null;
    return fetchPriceSnapshot(market === 'spot' ? 'okx-spot' : 'okx-swap', signal);
  }

  if (features && !(market === 'spot' ? features.priceSnapshot : features.futuresPriceFeed)) return null;

  return fetchPriceSnapshot(market, signal);
}

/** The usable entries of a ReceivePrice or ReceiveFuturesPrice argument; anything else in it is skipped. */
function ticksOf(batch: unknown): Tick[] {
  if (!Array.isArray(batch)) return [];

  const ticks: Tick[] = [];
  for (const item of batch as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const { symbol, lastPrice } = item as { symbol?: unknown; lastPrice?: unknown };
    if (typeof symbol === 'string' && typeof lastPrice === 'number') ticks.push({ symbol, lastPrice });
  }

  return ticks;
}

// ---------------------------------------------------------------------------------------------------------------------
// The feed

/** SignalR's own reconnects after a drop: right away, then 1, 2, 4, 8, 16 and 30 s, forever (never gives up). */
export const retryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds: ({ previousRetryCount }) => retryDelayMs(previousRetryCount),
};

function startPriceFeed({ createConnection = createSignalRConnection, loadSnapshot = loadPriceSnapshot, store = priceStore, exchange = currentExchangeSource }: PriceFeedOptions): () => void {
  /** Signed in, with the account's exchange known: a connection is wanted. */
  let running = false;
  /** The exchange whose prices the store holds and the connection carries. */
  let source: ExchangeId | null = null;
  /** Bumped whenever a connection is abandoned: callbacks of an older one are ignored. */
  let generation = 0;
  let connection: PriceHubConnection | null = null;
  /** A start is in flight. */
  let starting = false;
  /** Failed attempts in a row, for the backoff. */
  let attempt = 0;
  let everConnected = false;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let snapshotAbort: AbortController | null = null;

  function teardown(): void {
    generation += 1;
    starting = false;
    clearTimeout(retryTimer);
    retryTimer = undefined;
    snapshotAbort?.abort();
    snapshotAbort = null;

    const old = connection;
    connection = null;
    if (old) void old.stop().catch(() => undefined);
  }

  function scheduleRetry(): void {
    if (!running) return;

    clearTimeout(retryTimer);
    attempt += 1;
    setState('reconnecting');
    retryTimer = setTimeout(() => {
      retryTimer = undefined;
      void connect();
    }, retryDelayMs(attempt));
  }

  async function seedMarket(id: number, market: Market, signal: AbortSignal): Promise<void> {
    // Taken before the request: a tick that arrives while it is on its way is newer than the snapshot.
    const requestedAt = Date.now();

    try {
      const items = await loadSnapshot(market, signal, source ?? 'binance');
      if (id !== generation || signal.aborted || !items) return;
      store.applySnapshot(market, items, requestedAt, { fresh: !isSnapshotStalled(items, Date.now()) });
    } catch {
      // No snapshot: the prices from before the gap stay stale until live ticks confirm them.
    }
  }

  /** Seeds both markets from their snapshots, each with its own request time. */
  async function seed(id: number): Promise<void> {
    snapshotAbort?.abort();
    const controller = new AbortController();
    snapshotAbort = controller;

    try {
      await Promise.all(markets.map((market) => seedMarket(id, market, controller.signal)));
    } finally {
      if (snapshotAbort === controller) snapshotAbort = null;
    }
  }

  function connected(id: number): void {
    attempt = 0;
    everConnected = true;
    // Prices may have moved during the gap: everything known before this moment is stale until confirmed.
    const at = Date.now();
    store.markStaleBefore(at);
    setState('connected', at);
    void seed(id);
  }

  function watch(conn: PriceHubConnection, id: number, events: Record<Market, string>): void {
    for (const market of markets) {
      conn.on(events[market], (batch) => {
        if (id === generation) store.applyTicks(market, ticksOf(batch));
      });
    }
    conn.onreconnecting(() => {
      if (id === generation) setState('reconnecting');
    });
    conn.onreconnected(() => {
      if (id === generation) connected(id);
    });
    conn.onclose(() => {
      // Closed for good (SignalR gave up, or the server closed it): keep trying anyway.
      if (id !== generation) return;
      connection = null;
      scheduleRetry();
    });
  }

  async function connect(): Promise<void> {
    const id = ++generation;
    starting = true;
    setState(everConnected || attempt > 0 ? 'reconnecting' : 'connecting');

    const timeout = setTimeout(() => {
      if (id !== generation) return;
      teardown();
      scheduleRetry();
    }, startTimeoutMs);

    try {
      const wanted = source ?? 'binance';
      const conn = await createConnection(priceHubUrlFor(wanted), retryPolicy);
      if (id !== generation) return;

      connection = conn;
      watch(conn, id, priceEvents[wanted]);
      await conn.start();
    } catch {
      if (id === generation) {
        starting = false;
        connection = null;
        scheduleRetry();
      }
      return;
    } finally {
      clearTimeout(timeout);
    }

    if (id !== generation) return;
    starting = false;
    connected(id);
  }

  /** The phone woke up or came back online: do not wait for the backoff. */
  function reconnectNow(): void {
    if (!running) return;

    // Let a start in flight finish; if it fails, the next attempt follows in about a second.
    if (starting) {
      attempt = 0;
      return;
    }

    // A socket that died while the phone slept can still look connected: trust it only while ticks arrive (a snapshot
    // says nothing about the socket).
    if (state === 'connected') {
      const lastSign = Math.max(store.lastTickAt() ?? -Infinity, connectedAt ?? -Infinity);
      if (Date.now() - lastSign <= delayedAfterMs) return;
    }

    teardown();
    attempt = 0;
    void connect();
  }

  /**
   * Follows the session and the account's exchange. While the account is loading (just signed in, or switching), what
   * runs keeps running and nothing new starts. Another exchange drops the prices held: none of them may show under it.
   */
  function follow(): void {
    const signedIn = getSession() !== null;
    const wanted = signedIn ? exchange.get() : null;
    if (signedIn && wanted === null) return;
    if (signedIn === running && wanted === source) return;

    const otherExchange = source !== null && wanted !== source;
    running = signedIn;
    source = wanted;
    teardown();
    attempt = 0;
    everConnected = false;

    if (signedIn) {
      if (otherExchange) store.clear();
      void connect();
    } else {
      setState('disconnected');
      store.clear();
    }
  }

  const onOnline = () => reconnectNow();
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') reconnectNow();
  };

  const unsubscribeSession = subscribeSession(follow);
  const unsubscribeExchange = exchange.subscribe(follow);
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  follow();

  return () => {
    unsubscribeSession();
    unsubscribeExchange();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    running = false;
    teardown();
    setState('disconnected');
  };
}

let activeDispose: (() => void) | null = null;

/**
 * Starts the live price feed: one SignalR connection to /priceHub while signed in, stopped (and the prices cleared) at
 * sign-out. For a Binance account (?feeds=usdm) ReceivePrice goes to the spot prices and ReceiveFuturesPrice to the usdm
 * prices; for an OKX account (?feeds=okx) ReceiveOkxPrice and ReceiveOkxSwapPrice do, and switching between the two
 * exchanges clears the prices and reconnects. Reconnects forever
 * with backoff, and right away when the phone wakes up or comes back online. On every (re)connect the prices known
 * before it are marked stale, and both markets are seeded from their snapshots (when the API has them). Idempotent:
 * while the feed runs, another call returns the same dispose and its options are ignored.
 */
export function initPriceFeed(options: PriceFeedOptions = {}): () => void {
  if (activeDispose) return activeDispose;

  const stop = startPriceFeed(options);
  const dispose = () => {
    if (activeDispose !== dispose) return;
    activeDispose = null;
    stop();
  };

  activeDispose = dispose;
  return dispose;
}
